import type { ReactNode } from "react";
import type { Faq } from "@/payload-types";

export type FaqOverviewSectionEntry = {
	key: string;
	title: string;
	description: string;
	questions: Faq[];
};

export type FaqOverviewRenderedQuestion = {
	id: string;
	question: string;
	answer: ReactNode;
};

export type FaqOverviewRenderedSection = {
	key: string;
	title: string;
	description: string;
	questions: FaqOverviewRenderedQuestion[];
};

export type FaqOverviewCategoryOption = {
	value: string;
	label: string;
};

/**
 * Normalizes raw FAQ sections into renderable entries.
 *
 * @param sections - Sections from block props.
 * @returns Valid sections with object-based FAQ items only.
 */
export function normalizeFaqSections(sections: NonNullable<unknown>): FaqOverviewSectionEntry[] {
	if (!Array.isArray(sections)) {
		return [];
	}

	const normalized: FaqOverviewSectionEntry[] = [];

	for (let index = 0; index < sections.length; index++) {
		const section = sections[index] as { id?: string | null; title?: string | null; description?: string | null; questions?: unknown[] | null } | null;
		if (!section) {
			continue;
		}

		const title = typeof section.title === "string" ? section.title.trim() : "";
		const description = typeof section.description === "string" ? section.description.trim() : "";
		const questions = (section.questions ?? []).filter((item): item is Faq => typeof item === "object" && item !== null && !Array.isArray(item));

		if (questions.length === 0) {
			continue;
		}

		normalized.push({
			key: section.id ?? `section-${index}`,
			title,
			description,
			questions,
		});
	}

	return normalized;
}

/**
 * Builds category options from normalized sections.
 *
 * @param sections - Renderable FAQ sections.
 * @returns Combobox options for category filter.
 */
export function buildCategoryOptions(sections: Array<{ key: string; title: string }>): FaqOverviewCategoryOption[] {
	return sections
		.filter((section) => section.title.length > 0)
		.map((section) => ({
			value: section.key,
			label: section.title,
		}));
}

/**
 * Applies category and search filters to FAQ sections.
 *
 * @param sections - Renderable FAQ sections.
 * @param query - Fulltext query value.
 * @param category - Selected category key.
 * @returns Filtered sections preserving section grouping.
 */
export function filterFaqSections<TSection extends { key: string; questions: Array<{ question?: string | null }> }>(
	sections: TSection[],
	query: string,
	category: string,
): TSection[] {
	const normalizedQuery = query.trim().toLowerCase();
	const scopedSections = category.length > 0 ? sections.filter((section) => section.key === category) : sections;

	return scopedSections
		.map((section) => {
			const visibleQuestions =
				normalizedQuery.length > 0
					? section.questions.filter((item) => (item.question ?? "").toLowerCase().includes(normalizedQuery))
					: section.questions;

			return {
				...section,
				questions: visibleQuestions,
			};
		})
		.filter((section) => section.questions.length > 0);
}
